import { useCallback, useState } from "react";

/**
 * Unsent text, kept per view for as long as the tab lives. Added 2026-09-22.
 *
 * Three client composers lost everything on any navigation: the chat, a request's comment box and
 * the new-request dialog. A client halfway through describing a fault who stepped over to the
 * overview to check a number came back to an empty box, and the text they had typed existed nowhere.
 *
 * **A draft is a convenience, not a record.** It lives in `sessionStorage`, which is per tab and is
 * discarded when the tab closes, and that is the intended scope rather than a limitation: nothing here
 * leaves the browser, nothing is sent to the server, and a draft that outlived the tab would be text a
 * person typed on a shared computer and believed they had abandoned. Files are never stored, only
 * text and the few choices around it, because a `File` cannot be serialised and a draft that claimed
 * to hold one would be lying.
 *
 * The key carries the user and the tenant, `portal-draft:<userId>:<tenantId>:<view>`, so a person
 * with two tenants, or two people taking turns in one tab, never meet each other's half-written text.
 * A key of `null` means the identity has not answered yet, and the hook then holds the value in
 * memory only, which is what it also falls back to when storage throws.
 *
 * Every storage access is wrapped. Safari's private mode and a browser with site data blocked can
 * throw on `getItem` and `setItem` alike, and a composer that stopped accepting keystrokes because a
 * convenience failed would turn the convenience into the outage.
 *
 * Only client screens use this. Petrovic's admin screens do not, deliberately: they hold other
 * people's words, and a tab left open on a shared machine should not be carrying them around.
 */

const PREFIX = "portal-draft";

/** The storage key for one view, or null while either half of the identity is unknown. */
export function draftKey(
  userId: string | null | undefined,
  tenantId: string | null | undefined,
  view: string
): string | null {
  if (!userId || !tenantId) {
    return null;
  }
  return `${PREFIX}:${userId}:${tenantId}:${view}`;
}

function readStored(key: string | null): string | null {
  if (key === null) {
    return null;
  }
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string | null, serialised: string | null): void {
  if (key === null) {
    return;
  }
  try {
    if (serialised === null) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, serialised);
    }
  } catch {
    // Private mode or blocked storage. The value lives on in React state for this mount, which is
    // exactly what the composer had before drafts existed.
  }
}

/**
 * What was stored, if it is still the shape this caller expects.
 *
 * The default test is "the same JSON type as the initial value", which is enough for a string and
 * for a flat object whose fields the caller reads defensively. Anything else, including a value an
 * older build wrote in a different shape, reads as no draft rather than as a crash.
 */
function parse<T>(raw: string | null, initial: T, accept?: (value: unknown) => value is T): T {
  if (raw === null) {
    return initial;
  }
  try {
    const value: unknown = JSON.parse(raw);
    if (accept) {
      return accept(value) ? value : initial;
    }
    if (value === null || typeof value !== typeof initial) {
      return initial;
    }
    return value as T;
  } catch {
    return initial;
  }
}

/**
 * Every draft in this tab, removed. For sign-out.
 *
 * The keys already keep one person's drafts from another's, but sessionStorage outlives a sign-out
 * inside the same tab, so text somebody typed would sit in the browser until the tab closed. Nothing
 * calls this yet: sign-out lives in `SessionProvider.tsx`, and wiring it there is a change of its own.
 */
export function clearAllDrafts(): void {
  try {
    const storage = window.sessionStorage;
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key !== null && key.startsWith(`${PREFIX}:`)) {
        keys.push(key);
      }
    }
    for (const key of keys) {
      storage.removeItem(key);
    }
  } catch {
    // Storage that cannot be read holds no drafts this module could have written.
  }
}

/**
 * `[value, setValue, clear]`, with the value mirrored into `sessionStorage` under `key`.
 *
 * A value equal to `initial` removes the entry rather than storing it, so an emptied box leaves
 * nothing behind. `clear` is for the two moments a draft stops being wanted: the send or create
 * succeeded, or the person confirmed they wanted the text thrown away.
 *
 * The key can change under a mounted component, because the composers mount before the identity
 * read answers. When it does, the stored value for the new key is adopted during render, the
 * adjust-state-during-render form rather than an effect, so the box never paints empty and then
 * fills a frame later.
 */
export function useSessionDraft<T>(
  key: string | null,
  initial: T,
  accept?: (value: unknown) => value is T
): [T, (next: T) => void, () => void] {
  const initialJson = JSON.stringify(initial);

  const [state, setState] = useState(() => ({
    key,
    value: parse(readStored(key), initial, accept)
  }));

  let current = state;
  if (state.key !== key) {
    current = { key, value: parse(readStored(key), initial, accept) };
    setState(current);
  }

  const setValue = useCallback(
    (next: T) => {
      setState({ key, value: next });
      const serialised = JSON.stringify(next);
      writeStored(key, serialised === initialJson ? null : serialised);
    },
    [key, initialJson]
  );

  const clear = useCallback(() => {
    setState({ key, value: JSON.parse(initialJson) as T });
    writeStored(key, null);
  }, [key, initialJson]);

  return [current.value, setValue, clear];
}
